// utils
import { paramCase } from "../utils/change-case";
import { _id, _postTitles } from "../_mock/assets";

// ----------------------------------------------------------------------

const MOCK_ID = _id[1];

const MOCK_TITLE = _postTitles[2];

const ROOTS = {
  AUTH: "/auth",
  AUTH_DEMO: "/auth-demo",
  DASHBOARD: "/dashboard",
  MANAGEMENT: "/management",
  APP: "/app",
};

// ----------------------------------------------------------------------

export const paths = {
  comingSoon: "/coming-soon",
  maintenance: "/maintenance",
  pricing: "/pricing",
  payment: "/payment",
  about: "/about-us",
  contact: "/contact-us",
  faqs: "/faqs",
  page403: "/403",
  page404: "/404",
  page500: "/500",
  components: "/components",
  support: "https://soporte.consumannet.com.ar/",
  docs: "https://docs.minimals.cc",
  changelog: "https://docs.minimals.cc/changelog",
  zoneUI: "https://mui.com/store/items/zone-landing-page/",
  minimalUI: "https://mui.com/store/items/minimal-dashboard/",
  freeUI: "https://mui.com/store/items/minimal-dashboard-free/",
  figma:
    "https://www.figma.com/file/kAYnYYdib0aQPNKZpgJT6J/%5BPreview%5D-Minimal-Web.v5.0.0?type=design&node-id=0%3A1&t=Al4jScQq97Aly0Mn-1",
  product: {
    root: `/product`,
    checkout: `/product/checkout`,
    details: id => `/product/${id}`,
    demo: {
      details: `/product/${MOCK_ID}`,
    },
  },
  post: {
    root: `/post`,
    details: title => `/post/${paramCase(title)}`,
    demo: {
      details: `/post/${paramCase(MOCK_TITLE)}`,
    },
  },
  // AUTH
  auth: {
    login: `${ROOTS.AUTH}/login`,
    newPassword: `${ROOTS.AUTH}/new-password`,
    forgotPassword: `${ROOTS.AUTH}/forgot-password`,
    confirmEmailChange: `${ROOTS.AUTH}/confirm-email-change`,
    cancelEmailChange: `${ROOTS.AUTH}/cancel-email-change`,
    registerClaims: enterprise => `${ROOTS.AUTH}/register-claims/${encodeURIComponent(enterprise)}`,
    // External identity provider (OpenID Connect) login
    idp: `${ROOTS.AUTH}/idp`,
    idpWithEnterprise: enterpriseCode =>
      `${ROOTS.AUTH}/idp?enterprise=${encodeURIComponent(enterpriseCode)}`,
    idpCallback: enterpriseId => `${ROOTS.AUTH}/idp/callback/${enterpriseId}`,
    idpLogout: enterpriseId => `${ROOTS.AUTH}/idp/logout/${enterpriseId}`,
    // Claimed by the mobile app (Android App Links); the web only renders a fallback
    idpMobileCallback: enterpriseId => `${ROOTS.AUTH}/idp/mobile/callback/${enterpriseId}`,
    idpMobileLogout: enterpriseId => `${ROOTS.AUTH}/idp/mobile/logout/${enterpriseId}`,
  },
  authDemo: {
    classic: {
      login: `${ROOTS.AUTH_DEMO}/classic/login`,
      register: `${ROOTS.AUTH_DEMO}/classic/register`,
      forgotPassword: `${ROOTS.AUTH_DEMO}/classic/forgot-password`,
      newPassword: `${ROOTS.AUTH_DEMO}/classic/new-password`,
      verify: `${ROOTS.AUTH_DEMO}/classic/verify`,
    },
    modern: {
      login: `${ROOTS.AUTH_DEMO}/modern/login`,
      register: `${ROOTS.AUTH_DEMO}/modern/register`,
      forgotPassword: `${ROOTS.AUTH_DEMO}/modern/forgot-password`,
      newPassword: `${ROOTS.AUTH_DEMO}/modern/new-password`,
      verify: `${ROOTS.AUTH_DEMO}/modern/verify`,
    },
  },
  // DASHBOARD
  dashboard: {
    root: `${ROOTS.APP}`,
    welcome: `${ROOTS.APP}/welcome`,
    dashboard: `${ROOTS.APP}/dashboard`,
    mail: `${ROOTS.APP}/mail`,
    chat: `${ROOTS.APP}/chat`,
    blank: `${ROOTS.APP}/blank`,
    kanban: `${ROOTS.APP}/kanban`,
    calendar: `${ROOTS.APP}/calendar`,
    fileManager: `${ROOTS.APP}/file-manager`,
    permission: `${ROOTS.APP}/permission`,
    // Único path genérico que el backend necesita conocer para linkear cualquier entidad (ver
    // AIEntityLinks.cs). Sin id apunta a la grilla de esa entidad, con id a un registro puntual.
    // La página de destino real se resuelve en entity-redirect.js, que es la única fuente de
    // verdad de a qué ruta de este archivo corresponde cada entityType.
    open: (entityType, id) =>
      id ? `${ROOTS.APP}/open/${entityType}/${id}` : `${ROOTS.APP}/open/${entityType}`,
    general: {
      app: `${ROOTS.APP}/app`,
      ecommerce: `${ROOTS.APP}/ecommerce`,
      testPage: `${ROOTS.APP}/testPage`,
      analytics: `${ROOTS.APP}/analytics`,
      banking: `${ROOTS.APP}/banking`,
      booking: `${ROOTS.APP}/booking`,
      file: `${ROOTS.APP}/file`,
    },
    parameter: {
      root: `${ROOTS.APP}/parameter`,
      grid: `${ROOTS.APP}/parameter/grid`,
      form: `${ROOTS.APP}/parameter/form`,
      edit: id => `${ROOTS.APP}/parameter/${id}/form`,
      undelete: id => `${ROOTS.APP}/parameter/${id}/undelete`,
      copy: id => `${ROOTS.APP}/parameter/${id}/true/form`,
      import: `${ROOTS.APP}/parameter/import`,
    },
    parameterType: {
      root: `${ROOTS.APP}/parameter/parameter-type`,
      grid: `${ROOTS.APP}/parameter/parameter-type/grid`,
      form: `${ROOTS.APP}/parameter/parameter-type/form`,
      edit: id => `${ROOTS.APP}/parameter/parameter-type/${id}/form`,
      copy: id => `${ROOTS.APP}/parameter/parameter-type/${id}/true/form`,
    },
    plannedTask: {
      root: `${ROOTS.APP}/planned-task`,
      grid: `${ROOTS.APP}/planned-task/grid`,
      form: `${ROOTS.APP}/planned-task/form`,
      edit: id => `${ROOTS.APP}/planned-task/${id}/form`,
    },
    activityToDate: {
      root: `${ROOTS.APP}/activity-to-date`,
      form: `${ROOTS.APP}/activity-to-date/form`,
    },
    currency: {
      root: `${ROOTS.APP}/currency`,
      grid: `${ROOTS.APP}/currency/grid`,
    },
    category: {
      grid: `${ROOTS.APP}/category/grid`,
    },
    exchangeRate: {
      root: `${ROOTS.APP}/currency/exchange-rate`,
      grid: `${ROOTS.APP}/currency/exchange-rate/grid`,
    },
    audit: {
      root: `${ROOTS.APP}/audit`,
      grid: `${ROOTS.APP}/audit/grid`,
      entityGrid: (entityName, entityId) => `${ROOTS.APP}/audit/${entityName}/${entityId}/grid`,
    },
    supply: {
      root: `${ROOTS.APP}/supply`,
      grid: `${ROOTS.APP}/supply/grid`,
      form: `${ROOTS.APP}/supply/form`,
      edit: id => `${ROOTS.APP}/supply/${id}/form`,
      copy: id => `${ROOTS.APP}/supply/${id}/true/form`,
      attachments: id => `${ROOTS.APP}/supply/${id}/attachments`,
      undelete: id => `${ROOTS.APP}/supply/${id}/undelete`,
      view: id => `${ROOTS.APP}/supply/${id}/detail`,
      import: `${ROOTS.APP}/supply/import`,
    },
    importEntity: {
      form: (id, endpoint) => `${ROOTS.APP}/import-entity/${id}/${endpoint}/form`,
    },
    fieldConfiguration: {
      edit: id => `${ROOTS.APP}/field-configuration/${id}/form`,
    },
    provider: {
      root: `${ROOTS.APP}/provider`,
      form: `${ROOTS.APP}/provider/form`,
      grid: `${ROOTS.APP}/provider/grid`,
      edit: id => `${ROOTS.APP}/provider/${id}/form`,
      undelete: id => `${ROOTS.APP}/provider/${id}/undelete`,
      copy: id => `${ROOTS.APP}/provider/${id}/true/form`,
      attachments: id => `${ROOTS.APP}/provider/${id}/attachments`,
      import: `${ROOTS.APP}/provider/import`,
    },
    workRequest: {
      root: `${ROOTS.APP}/work-request`,
      form: `${ROOTS.APP}/work-request/form`,
      grid: `${ROOTS.APP}/work-request/grid`,
      category: category => `${ROOTS.APP}/work-request/${category}/category`,
      edit: id => `${ROOTS.APP}/work-request/${id}/form`,
      copy: id => `${ROOTS.APP}/work-request/${id}/true/form`,
      copyType: (id, type) => `${ROOTS.APP}/work-request/${id}/${type}/form`,
      attachments: id => `${ROOTS.APP}/work-request/${id}/attachments`,
      conformity: id => `${ROOTS.APP}/work-request/${id}/conformity`,
      conformityRating: (id, rating) => `${ROOTS.APP}/work-request/${id}/conformity/${rating}`,
    },
    program: {
      root: `${ROOTS.APP}/program`,
      form: `${ROOTS.APP}/program/form`,
      grid: `${ROOTS.APP}/program/grid`,
      edit: id => `${ROOTS.APP}/program/${id}/form`,
      copy: id => `${ROOTS.APP}/program/${id}/true/form`,
      import: `${ROOTS.APP}/program/import`,
    },
    task: {
      root: `${ROOTS.APP}/task`,
      new: `${ROOTS.APP}/task/form`,
      grid: `${ROOTS.APP}/task/grid`,
      edit: id => `${ROOTS.APP}/task/${id}/form`,
      undelete: id => `${ROOTS.APP}/task/${id}/undelete`,
      copy: id => `${ROOTS.APP}/task/${id}/true/form`,
      import: `${ROOTS.APP}/task/import`,
      attachments: id => `${ROOTS.APP}/task/${id}/attachments`,
    },
    taskGroup: {
      root: `${ROOTS.APP}/work-order-requirement`,
      new: `${ROOTS.APP}/work-order-requirement/form`,
      grid: `${ROOTS.APP}/work-order-requirement/grid`,
      edit: id => `${ROOTS.APP}/work-order-requirement/${id}/form`,
      undelete: id => `${ROOTS.APP}/work-order-requirement/${id}/undelete`,
      copy: id => `${ROOTS.APP}/work-order-requirement/${id}/true/form`,
    },
    equipmentStop: {
      root: `${ROOTS.APP}/equipment-stop/`,
      new: `${ROOTS.APP}/equipment-stop/form`,
      grid: `${ROOTS.APP}/equipment-stop/grid`,
      edit: id => `${ROOTS.APP}/equipment-stop/${id}/form`,
      copy: id => `${ROOTS.APP}/equipment-stop/${id}/true/form`,
    },
    scheduledOperation: {
      root: `${ROOTS.APP}/equipment-stop/scheduled-operation`,
      new: `${ROOTS.APP}/equipment-stop/scheduled-operation/form`,
      grid: `${ROOTS.APP}/equipment-stop/scheduled-operation/grid`,
      edit: id => `${ROOTS.APP}/equipment-stop/scheduled-operation/${id}/form`,
      copy: id => `${ROOTS.APP}/equipment-stop/scheduled-operation/${id}/true/form`,
    },
    structure: {
      root: `${ROOTS.APP}/structure`,
      new: `${ROOTS.APP}/structure/form`,
      grid: `${ROOTS.APP}/structure/grid`,
      edit: id => `${ROOTS.APP}/structure/${id}/form`,
      undelete: id => `${ROOTS.APP}/structure/${id}/undelete`,
      copy: id => `${ROOTS.APP}/structure/${id}/true/form`,
      import: `${ROOTS.APP}/structure/import`,
    },
    role: {
      root: `${ROOTS.APP}/user/user-role`,
      grid: `${ROOTS.APP}/user/user-role/grid`,
      new: `${ROOTS.APP}/user/user-role/form`,
      edit: id => `${ROOTS.APP}/user/user-role/${id}/form`,
      undelete: id => `${ROOTS.APP}/user/user-role/${id}/undelete`,
      copy: id => `${ROOTS.APP}/user/user-role/${id}/true/form`,
    },
    user: {
      root: `${ROOTS.APP}/user`,
      new: `${ROOTS.APP}/user/form`,
      import: `${ROOTS.APP}/user/import`,
      grid: `${ROOTS.APP}/user/grid`,
      profile: `${ROOTS.APP}/user/profile`,
      undelete: id => `${ROOTS.APP}/user/${id}/undelete`,
      edit: id => `${ROOTS.APP}/user/${id}/form`,
      copy: id => `${ROOTS.APP}/user/${id}/true/form`,
      attachments: id => `${ROOTS.APP}/user/${id}/attachments`,
      view: id => `${ROOTS.APP}/user/${id}/detail`,
    },
    product: {
      root: `${ROOTS.APP}/product`,
      new: `${ROOTS.APP}/product/new`,
      details: id => `${ROOTS.APP}/product/${id}`,
      edit: id => `${ROOTS.APP}/product/${id}/edit`,
      demo: {
        details: `${ROOTS.APP}/product/${MOCK_ID}`,
        edit: `${ROOTS.APP}/product/${MOCK_ID}/edit`,
      },
    },
    invoice: {
      root: `${ROOTS.APP}/invoice`,
      new: `${ROOTS.APP}/invoice/new`,
      details: id => `${ROOTS.APP}/invoice/${id}`,
      edit: id => `${ROOTS.APP}/invoice/${id}/edit`,
      demo: {
        details: `${ROOTS.APP}/invoice/${MOCK_ID}`,
        edit: `${ROOTS.APP}/invoice/${MOCK_ID}/edit`,
      },
    },
    post: {
      root: `${ROOTS.APP}/post`,
      new: `${ROOTS.APP}/post/new`,
      details: title => `${ROOTS.APP}/post/${paramCase(title)}`,
      edit: title => `${ROOTS.APP}/post/${paramCase(title)}/edit`,
      demo: {
        details: `${ROOTS.APP}/post/${paramCase(MOCK_TITLE)}`,
        edit: `${ROOTS.APP}/post/${paramCase(MOCK_TITLE)}/edit`,
      },
    },
    order: {
      root: `${ROOTS.APP}/order`,
      details: id => `${ROOTS.APP}/order/${id}`,
      demo: {
        details: `${ROOTS.APP}/order/${MOCK_ID}`,
      },
    },
    job: {
      root: `${ROOTS.APP}/job`,
      new: `${ROOTS.APP}/job/new`,
      details: id => `${ROOTS.APP}/job/${id}`,
      edit: id => `${ROOTS.APP}/job/${id}/edit`,
      demo: {
        details: `${ROOTS.APP}/job/${MOCK_ID}`,
        edit: `${ROOTS.APP}/job/${MOCK_ID}/edit`,
      },
    },
    tour: {
      root: `${ROOTS.APP}/tour`,
      new: `${ROOTS.APP}/tour/new`,
      details: id => `${ROOTS.APP}/tour/${id}`,
      edit: id => `${ROOTS.APP}/tour/${id}/edit`,
      demo: {
        details: `${ROOTS.APP}/tour/${MOCK_ID}`,
        edit: `${ROOTS.APP}/tour/${MOCK_ID}/edit`,
      },
    },
    system: {
      root: `${ROOTS.APP}/system`,
      labels: `${ROOTS.APP}/system/labels`,
      globalConfiguration: `${ROOTS.APP}/system/globalConfiguration`,
    },
    location: {
      form: `${ROOTS.APP}/location/form`,
      edit: id => `${ROOTS.APP}/asset/location/${id}/form`,
    },
    equipment: {
      form: `${ROOTS.APP}/equipment/form`,
      edit: id => `${ROOTS.APP}/asset/equipment/${id}/form`,
    },
    component: {
      root: `${ROOTS.APP}/component`,
      form: `${ROOTS.APP}/component/form`,
      grid: `${ROOTS.APP}/component/grid`,
      edit: id => `${ROOTS.APP}/component/${id}/form`,
      copy: id => `${ROOTS.APP}/component/${id}/true/form`,
      undelete: id => `${ROOTS.APP}/component/${id}/undelete`,
      import: `${ROOTS.APP}/component/import`,
    },
    asset: {
      root: `${ROOTS.APP}/asset`,
      new: `${ROOTS.APP}/asset/form`,
      newLocationFromParent: id => `${ROOTS.APP}/asset/location/${id}/with-parent`,
      newEquipmentFromParent: id => `${ROOTS.APP}/asset/equipment/${id}/with-parent`,
      editLocation: id => `${ROOTS.APP}/asset/location/${id}/form`,
      editEquipment: id => `${ROOTS.APP}/asset/equipment/${id}/form`,
      viewLocation: id => `${ROOTS.APP}/asset/location/${id}/detail`,
      viewEquipment: id => `${ROOTS.APP}/asset/equipment/${id}/detail`,
      attachmentsLocation: id => `${ROOTS.APP}/asset/location/${id}/attachments`,
      attachmentsEquipment: id => `${ROOTS.APP}/asset/equipment/${id}/attachments`,
      copyEquipment: id => `${ROOTS.APP}/asset/equipment/${id}/copy`,
      undeleteEquipment: id => `${ROOTS.APP}/asset/equipment/${id}/undelete`,
      copyLocation: id => `${ROOTS.APP}/asset/location/${id}/copy`,
      undeleteLocation: id => `${ROOTS.APP}/asset/location/${id}/undelete`,
      grid: `${ROOTS.APP}/asset/grid`,
      import: `${ROOTS.APP}/asset/import`,
    },
    workTeam: {
      root: `${ROOTS.APP}/user/workTeam`,
      new: `${ROOTS.APP}/user/workTeam/form`,
      grid: `${ROOTS.APP}/user/workTeam/grid`,
      edit: id => `${ROOTS.APP}/user/workTeam/${id}/form`,
      view: id => `${ROOTS.APP}/user/workTeam/${id}/detail`,
    },
    workOrder: {
      root: `${ROOTS.APP}/work-order`,
      grid: `${ROOTS.APP}/work-order/grid`,
      edit: id => `${ROOTS.APP}/work-order/${id}/form`,
      document: id => `${ROOTS.APP}/work-order/${id}/document`,
    },
    workOrderTask: {
      root: `${ROOTS.APP}/work-order-task`,
      edit: id => `${ROOTS.APP}/work-order-task/${id}/form`,
      attachments: id => `${ROOTS.APP}/work-order-task/${id}/attachments`,
    },
    woTask: {
      form: `${ROOTS.APP}/work-order/grid`,
    },
    meter: {
      root: `${ROOTS.APP}/meter`,
      new: `${ROOTS.APP}/meter/form`,
      grid: `${ROOTS.APP}/meter/grid`,
      edit: id => `${ROOTS.APP}/meter/${id}/form`,
      undelete: id => `${ROOTS.APP}/meter/${id}/undelete`,
      view: id => `${ROOTS.APP}/meter/${id}/detail`,
    },
    reading: {
      root: `${ROOTS.APP}/meter/reading`,
      grid: `${ROOTS.APP}/meter/reading/grid`,
      importReading: `${ROOTS.APP}/meter/reading/readings/import`,
    },
    voucher: {
      root: `${ROOTS.APP}/inventory`,
      form: `${ROOTS.APP}/inventory/form`,
      grid: `${ROOTS.APP}/inventory/grid`,
      edit: id => `${ROOTS.APP}/inventory/${id}/form`,
      attachments: id => `${ROOTS.APP}/inventory/${id}/attachments`,
      document: id => `${ROOTS.APP}/inventory/${id}/document`,
    },
    reportsDashboard: {
      root: `${ROOTS.APP}/reports-dashboard`,
      grid: key => `${ROOTS.APP}/reports-dashboard/${key}/grid`,
    },
    voucherType: {
      root: `${ROOTS.APP}/inventory/voucher-type`,
      form: `${ROOTS.APP}/inventory/voucher-type/form`,
      grid: `${ROOTS.APP}/inventory/voucher-type/grid`,
      edit: id => `${ROOTS.APP}/inventory/voucher-type/${id}/form`,
    },
    inventory: {
      root: `${ROOTS.APP}/inventory`,
      grid: `${ROOTS.APP}/inventory/grid`,
    },
  },
  management: {
    root: ROOTS.MANAGEMENT,
    enterprise: {
      root: `${ROOTS.MANAGEMENT}/enterprise`,
      new: `${ROOTS.MANAGEMENT}/enterprise/form`,
      grid: `${ROOTS.MANAGEMENT}/enterprise/grid`,
      edit: id => `${ROOTS.MANAGEMENT}/enterprise/${id}/form`,
    },
    gpt: {
      root: `${ROOTS.MANAGEMENT}/enterprise`,
      edit: `${ROOTS.MANAGEMENT}/gpt`,
    },
    legacyMigration: {
      root: `${ROOTS.MANAGEMENT}/legacy-migration`,
    },
  },
};
